/**
 * ContentPlanner — full-screen, channel-separated planner for one source piece.
 *
 *   /portal/content-calendar/plan/:sourceType/:slug
 *
 * Replaces the old right-side slide-over. Each social channel gets its own
 * full-width tab/lane so they NEVER bleed into each other — Instagram,
 * LinkedIn Personal, LinkedIn HV Page, Facebook, plus future-proofed
 * YouTube and TikTok tabs (marked "soon").
 *
 * One CHANNELS registry below is the single source of truth — adding
 * Facebook fully, or wiring up YouTube/TikTok later, is a one-line change.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Sparkles } from "lucide-react";
import {
  BLOG_POSTS, RESOURCES, VIDEOS,
} from "../lib/content/insights";
import {
  DraftCard, SOURCE_LABELS,
} from "../components/content/DraftCard";
import { BufferProvider, useBuffer, channelsForAngle } from "../components/content/BufferContext";
import { Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_ADMIN = "hv_admin_token";

/* ─── Channel registry — the single source of truth ─────────────────── */
const CHANNELS = [
  {
    key: "instagram",
    label: "Instagram",
    short: "IG",
    angle: "instagram",
    target: "personal",
    enabled: true,
    matches: (d) => d.angle === "instagram",
  },
  {
    key: "linkedin-company",
    label: "LinkedIn — HidroVerse Page",
    short: "LinkedIn",
    angle: "linkedin-text",
    target: "company",
    enabled: true,
    // Catch ALL linkedin drafts (any target_channel). LinkedIn Personal lane
    // was retired Feb 2026 — Buffer Free can't run separate text for personal
    // vs company in one compose. We post via HV Page only and let team
    // members re-share to their personal feeds manually if they want to.
    matches: (d) => (d.angle || "").startsWith("linkedin"),
  },
  {
    key: "facebook",
    label: "Facebook",
    short: "FB",
    angle: "facebook",
    target: "personal",
    enabled: true,
    matches: (d) => d.angle === "facebook",
  },
  {
    key: "youtube",
    label: "YouTube",
    short: "YT",
    angle: "youtube",
    target: "personal",
    enabled: false, comingSoon: true,
    matches: (d) => d.angle === "youtube",
  },
  {
    key: "tiktok",
    label: "TikTok",
    short: "TT",
    angle: "tiktok",
    target: "personal",
    enabled: false, comingSoon: true,
    matches: (d) => d.angle === "tiktok",
  },
];

/* For launch-week seeded posts, map slug → asset-prefix so we can show the
   full 5-variant gallery per channel. Future posts without pre-rendered
   assets simply get an empty gallery (the editor still works). */
const ASSET_PREFIX = {
  "launch-announcement":     "launch",
  "reasoning-not-brochures": "wed",
  // Friday Thornybush video has no static stills — leave unmapped.
};

function assetsForChannel(channelKey, sourceSlug) {
  const prefix = ASSET_PREFIX[sourceSlug];
  if (!prefix) return [];
  const map = {
    "linkedin-company":  [`${prefix}-li-1200x627.png`, `${prefix}-li-1200x1200.png`],
    "instagram":         [`${prefix}-ig-1080x1080.png`,
                          `${prefix}-ig-1080x1350.png`,
                          `${prefix}-story-1080x1920.png`],
    "facebook":          [`${prefix}-li-1200x627.png`, `${prefix}-ig-1080x1080.png`],
    "youtube":           [`${prefix}-story-1080x1920.png`],
    "tiktok":            [`${prefix}-story-1080x1920.png`],
  };
  return (map[channelKey] || []).map((f) => `/og/${f}`);
}

/* Resolve source metadata from the static content files, given the URL params.
   Falls back to a tiny title-only stub if it's a synthetic source (e.g. the
   "launch-announcement" seed which doesn't live in BLOG_POSTS). */
function resolveSource(sourceType, slug, drafts) {
  if (sourceType === "insight") {
    const p = BLOG_POSTS.find((x) => x.slug === slug);
    if (p) return { source_type: "insight", slug, title: p.title,
                    summary: p.excerpt || "", body_preview: p.excerpt || "",
                    tags: p.tags || [], href: `/insights/${slug}` };
  }
  if (sourceType === "video") {
    const v = VIDEOS.find((x) => x.slug === slug);
    if (v) return { source_type: "video", slug, title: v.title,
                    summary: v.caption || "", body_preview: v.caption || "",
                    tags: v.tags || [], project_slug: v.project_slug,
                    filmed_at: v.filmed_at };
  }
  if (sourceType === "resource") {
    const r = RESOURCES.find((x) => x.slug === slug);
    if (r) return { source_type: "resource", slug, title: r.title,
                    summary: r.body || "", body_preview: r.body || "",
                    tags: r.tags || [], href: `/insights/resource/${slug}` };
  }
  // Fallback — use the source_title from any existing draft.
  const d = drafts.find((x) => x.source_slug === slug);
  return {
    source_type: sourceType,
    slug,
    title: d ? d.source_title.replace(/\s*\(.*\)$/, "") : slug,
    summary: "", body_preview: "", tags: [],
  };
}

export default function ContentPlanner() {
  const token = localStorage.getItem(STORAGE_ADMIN) || "";
  return (
    <BufferProvider token={token}>
      <PlannerInner />
    </BufferProvider>
  );
}

function PlannerInner() {
  const { sourceType, slug } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(STORAGE_ADMIN) || "";

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [activeChannel, setActiveChannel] = useState("instagram");
  const [brief, setBrief] = useState("");
  const [showBrief, setShowBrief] = useState(false);

  /* Redirect to login if no token */
  useEffect(() => {
    if (!token) {
      navigate("/portal/content-calendar", { replace: true });
    }
  }, [token, navigate]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const r = await fetch(
        `${API}/api/content-calendar/drafts?source_slug=${encodeURIComponent(slug)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.status === 401 || r.status === 403) {
        localStorage.removeItem(STORAGE_ADMIN);
        navigate("/portal/content-calendar", { replace: true });
        return;
      }
      const d = await r.json();
      setDrafts(d.drafts || []);
    } catch (e) {
      setError("Could not load drafts.");
    } finally {
      setLoading(false);
    }
  }, [slug, token, navigate]);

  useEffect(() => { load(); }, [load]);

  const source = useMemo(
    () => resolveSource(sourceType, slug, drafts),
    [sourceType, slug, drafts],
  );

  /* Bucket drafts per channel using the registry matchers. */
  const draftsByChannel = useMemo(() => {
    const m = {};
    for (const ch of CHANNELS) m[ch.key] = [];
    for (const d of drafts) {
      for (const ch of CHANNELS) {
        if (ch.matches(d)) { m[ch.key].push(d); break; }
      }
    }
    return m;
  }, [drafts]);

  /* On first load, jump to the first channel that already has a draft, else IG. */
  useEffect(() => {
    if (loading) return;
    const firstWithDraft = CHANNELS.find(
      (ch) => ch.enabled && (draftsByChannel[ch.key] || []).length > 0,
    );
    if (firstWithDraft) setActiveChannel(firstWithDraft.key);
  }, [loading, draftsByChannel]);

  const updateDraft = async (id, patch) => {
    const r = await fetch(`${API}/api/content-calendar/drafts/${id}`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      throw new Error(data.detail || "Could not update");
    }
    await load();
  };

  // Silent autosave — PATCHes without triggering a full reload (no
  // loading spinner, no scroll jump, no DraftCard remount). Updates the
  // local drafts array in-place so subsequent edits stay in sync.
  const updateDraftSilent = useCallback(async (id, patch) => {
    const r = await fetch(`${API}/api/content-calendar/drafts/${id}`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      throw new Error(data.detail || "autosave failed");
    }
    const data = await r.json().catch(() => null);
    const updated = data && data.draft ? data.draft : null;
    if (updated && updated.id) {
      setDrafts((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)));
    }
    return updated;
  }, [token]);

  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    const r = await fetch(`${API}/api/content-calendar/drafts/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { setError("Could not delete draft"); return; }
    await load();
  };

  /* Generate drafts — optionally limited to a single channel angle. */
  const generate = async ({ angles, regenerate = false, briefText } = {}) => {
    setGenerating(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/content-calendar/generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          source: {
            source_type: source.source_type,
            slug: source.slug,
            title: source.title,
            summary: source.summary || "",
            body_preview: source.body_preview || "",
            tags: source.tags || [],
            project_slug: source.project_slug,
            filmed_at: source.filmed_at,
          },
          angles: angles || undefined,
          regenerate,
          brief: briefText || undefined,
        }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || "Could not generate drafts");
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  /* Cross-channel brief — single instruction that fans out to every enabled
     channel. Wipes 'idea' / 'drafted' rows and regenerates all of them in one
     pass. */
  const runCrossChannelBrief = async () => {
    if (!brief.trim()) { setError("Write a brief first."); return; }
    if (!window.confirm(
      "Generate fresh drafts for ALL enabled channels using this brief? Drafts " +
      "in 'idea' or 'drafted' state will be replaced.",
    )) return;
    const enabledAngles = CHANNELS
      .filter((c) => c.enabled)
      .map((c) => c.angle);
    await generate({ angles: enabledAngles, regenerate: true, briefText: brief.trim() });
  };

  const LAUNCH_SLUGS = ["launch-announcement", "reasoning-not-brochures",
                        "thornybush-borehole-pull"];
  const isLaunchPost = LAUNCH_SLUGS.includes(slug);

  const restoreWeekOne = async () => {
    if (!window.confirm(
      "Wipe ALL current Week-1 launch drafts (Monday + Wednesday + Friday across LinkedIn HV Page and Instagram) and restore the locked-in approved harmonised copy?\n\nThis cannot be undone.",
    )) return;
    setGenerating(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/content-calendar/restore-week-1`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not restore");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const totalDrafts = drafts.length;
  const activeMeta = CHANNELS.find((c) => c.key === activeChannel) || CHANNELS[0];
  const activeDrafts = draftsByChannel[activeChannel] || [];

  return (
    <main
      className="min-h-screen"
      style={{ background: "#FAFAF7", color: "#0C2A40" }}
      data-testid="content-planner"
    >
      <Helmet>
        <title>{`Planner — ${source.title} — HidroVerse`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      {/* ─── Top bar ─────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-30"
           style={{ background: "#16181B", color: "#F5F7FA",
                    borderBottom: "1px solid rgba(31,182,193,0.25)" }}>
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4 flex-wrap">
          <Link
            to="/portal/content-calendar"
            data-testid="planner-back"
            className="flex items-center gap-1.5 font-mono uppercase"
            style={{ fontSize: "0.62rem", letterSpacing: "0.18em",
                     color: "rgba(245,247,250,0.85)", textDecoration: "none" }}
          >
            <ArrowLeft size={13} /> Back to calendar
          </Link>
          <div className="hidden sm:block"
               style={{ height: 20, width: 1, background: "rgba(245,247,250,0.2)" }} />
          <div className="min-w-0 flex-1">
            <div className="font-mono uppercase"
                 style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                          color: "#1FB6C1", fontWeight: 700 }}>
              {SOURCE_LABELS[source.source_type] || source.source_type} · {totalDrafts} drafts across all channels
            </div>
            <h1 className="truncate"
                style={{ fontSize: "1.1rem", fontWeight: 600, marginTop: 2 }}>
              {source.title}
            </h1>
          </div>
          {isLaunchPost && (
            <button
              type="button"
              onClick={restoreWeekOne}
              disabled={generating}
              data-testid="planner-restore-week-1"
              className="px-3 py-1.5 font-mono uppercase flex items-center gap-1.5"
              style={{ background: "transparent", color: "#F5F7FA",
                       border: "1px solid rgba(31,182,193,0.40)",
                       fontSize: "0.62rem", letterSpacing: "0.15em",
                       fontWeight: 700,
                       cursor: generating ? "wait" : "pointer",
                       opacity: generating ? 0.5 : 1, borderRadius: 999 }}
              title="Wipes the Week-1 launch drafts and restores the locked-in approved harmonised copy"
            >
              <RefreshCw size={11} /> Restore approved Week-1
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (window.confirm("Regenerate drafts for ALL channels? Drafts in 'idea' or 'drafted' state will be replaced.")) {
                generate({ regenerate: true });
              }
            }}
            disabled={generating}
            data-testid="planner-regenerate-all"
            className="px-3 py-1.5 font-mono uppercase flex items-center gap-1.5"
            style={{ background: "#1FB6C1", color: "#0C2A40",
                     fontSize: "0.62rem", letterSpacing: "0.15em",
                     fontWeight: 700, border: "none",
                     cursor: generating ? "wait" : "pointer",
                     opacity: generating ? 0.5 : 1, borderRadius: 999 }}
          >
            <RefreshCw size={11} /> {generating ? "Regenerating…" : "Regenerate all"}
          </button>
        </div>

        {/* ─── Channel tabs ─────────────────────────────────────────── */}
        <div className="max-w-[1600px] mx-auto px-2 sm:px-6 flex flex-wrap"
             data-testid="planner-channel-tabs">
          {CHANNELS.map((ch) => {
            const n = (draftsByChannel[ch.key] || []).length;
            const active = ch.key === activeChannel;
            return (
              <button
                key={ch.key}
                type="button"
                onClick={() => ch.enabled && setActiveChannel(ch.key)}
                disabled={!ch.enabled}
                data-testid={`planner-tab-${ch.key}`}
                className="px-4 py-2.5 font-mono uppercase flex items-center gap-2"
                style={{ fontSize: "0.62rem", letterSpacing: "0.18em",
                  fontWeight: 700,
                  background: "transparent",
                  color: active ? "#F5F7FA" : (ch.enabled ? "rgba(245,247,250,0.55)" : "rgba(245,247,250,0.30)"),
                  borderBottom: active ? "2px solid #1FB6C1" : "2px solid transparent",
                  cursor: ch.enabled ? "pointer" : "not-allowed",
                  border: "none", borderRadius: 999 }}
              >
                <span>{ch.short}</span>
                {n > 0 && (
                  <span className="px-1.5 py-0.5"
                        style={{ background: active ? "#1FB6C1" : "rgba(31,182,193,0.25)",
                                 color: active ? "#0C2A40" : "#1FB6C1",
                                 fontSize: "0.55rem", letterSpacing: "0.10em",
                                 borderRadius: 2 }}>
                    {n}
                  </span>
                )}
                {ch.comingSoon && (
                  <span style={{ fontSize: "0.55rem", letterSpacing: "0.10em",
                                 color: "rgba(245,247,250,0.40)" }}>
                    soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Lane — full-screen content for the active channel ────────── */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6"
           data-testid={`planner-lane-${activeChannel}`}>

        {/* Buffer workflow helper — sits above every lane so the team
            doesn't forget how to schedule on Buffer Free (one channel at a
            time, no per-channel text customisation). */}
        <div className="mb-4 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1"
             style={{ background: "rgba(31,182,193,0.06)",
                      border: "1px solid rgba(31,182,193,0.30)",
                      color: "rgba(12,42,64,0.80)",
                      fontSize: "0.78rem", lineHeight: 1.45,
                      borderRadius: 10 }}
             data-testid="planner-buffer-note">
          <span className="font-mono uppercase"
                style={{ fontSize: "0.6rem", letterSpacing: "0.18em",
                         color: "#0E7A82", fontWeight: 700 }}>
            Buffer workflow
          </span>
          <span>
            Schedule each channel as its own Buffer post — select <b>only one
            channel</b> in the composer per draft. Buffer Free shares the
            same text across multi-selected channels.
          </span>
        </div>

        {/* ─── Cross-channel brief ─────────────────────────────────────
            One prompt → fresh drafts for every enabled channel. Lets the
            editor steer all three (IG / LinkedIn / Facebook) with a single
            instruction instead of refining each lane separately. */}
        <div className="hv-card-soft mb-4"
             style={{ padding: 12 }}
             data-testid="planner-cross-channel-brief">
          <button
            type="button"
            onClick={() => setShowBrief((s) => !s)}
            data-testid="planner-cross-channel-toggle"
            className="w-full flex items-center justify-between gap-2"
            style={{ background: "transparent", border: "none",
                     cursor: "pointer", padding: 0 }}
          >
            <div className="flex items-center gap-2">
              <Sparkles size={14} style={{ color: "#0E7A82" }} />
              <span className="font-mono uppercase"
                    style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                             color: "#0E7A82", fontWeight: 700 }}>
                Cross-channel brief — one prompt, every channel
              </span>
            </div>
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                           color: "rgba(12,42,64,0.55)" }}>
              {showBrief ? "Hide" : "Open"}
            </span>
          </button>

          {showBrief && (
            <div className="mt-3">
              <p className="mb-2 font-light"
                 style={{ fontSize: "0.82rem",
                          color: "rgba(12,42,64,0.70)", lineHeight: 1.5 }}>
                Write a single brief and we'll regenerate every enabled
                channel (Instagram · LinkedIn HV Page · Facebook) following
                that brief. Per-channel voice rules still apply on top.
              </p>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && brief.trim()) {
                    runCrossChannelBrief();
                  }
                }}
                rows={3}
                placeholder={
                  "Tell the AI how to position this across all channels — e.g.\n" +
                  "  • \"Lead with the maintenance angle, mention same-day diagnostic, end with a clear CTA to /contact\"\n" +
                  "  • \"Make it punchier, shorter, more direct — Dirk-on-site tone\""
                }
                data-testid="planner-cross-channel-prompt"
                className="hv-input w-full"
                style={{ resize: "vertical", fontSize: "0.88rem" }}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={runCrossChannelBrief}
                  disabled={generating || !brief.trim()}
                  data-testid="planner-cross-channel-run"
                  className="hv-pill is-active"
                >
                  <Sparkles size={11} />
                  {generating ? "Generating all channels…" : "Generate every channel"}
                </button>
                {brief && (
                  <button
                    type="button"
                    onClick={() => setBrief("")}
                    disabled={generating}
                    data-testid="planner-cross-channel-clear"
                    className="hv-pill"
                  >
                    Clear
                  </button>
                )}
                <span className="font-mono uppercase ml-auto"
                      style={{ fontSize: "0.58rem", letterSpacing: "0.16em",
                               color: "rgba(12,42,64,0.5)" }}>
                  ⌘ / Ctrl + Enter
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ─── Buffer "Push everything" panel ─────────────────────────
            One click → posts every enabled channel's latest approved
            draft to Buffer's queue (or schedules them for a chosen
            time).  Single-channel pushes live on each DraftCard. */}
        <PushAllPanel
          drafts={drafts}
          channels={CHANNELS}
          sourceSlug={slug}
          token={token}
          onPushed={load}
        />

        {error && (
          <div className="mb-4 px-4 py-3"
               style={{ background: "rgba(184,92,56,0.10)",
                        border: "1px solid rgba(184,92,56,0.35)",
                        color: "#B85C38", fontSize: "0.85rem", borderRadius: 10 }}
               data-testid="planner-error">
            {error}
          </div>
        )}

        {/* Lane header */}
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono uppercase"
                 style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                          color: "rgba(12,42,64,0.55)" }}>
              Channel
            </div>
            <h2 style={{ fontSize: "1.3rem", fontWeight: 700, marginTop: 2 }}>
              {activeMeta.label}
              {activeMeta.comingSoon && (
                <span className="ml-3 px-2 py-0.5 font-mono uppercase"
                      style={{ fontSize: "0.55rem", letterSpacing: "0.15em",
                               color: "rgba(12,42,64,0.55)",
                               background: "rgba(12,42,64,0.05)",
                               border: "1px solid rgba(12,42,64,0.20)" }}>
                  Coming soon
                </span>
              )}
            </h2>
          </div>

          {activeMeta.enabled && activeDrafts.length === 0 && (
            <button
              type="button"
              onClick={() => generate({ angles: [activeMeta.angle] })}
              disabled={generating}
              data-testid={`planner-generate-channel-${activeChannel}`}
              className="px-3 py-1.5 font-mono uppercase flex items-center gap-1.5"
              style={{ background: "#1FB6C1", color: "#0C2A40",
                       fontSize: "0.62rem", letterSpacing: "0.15em",
                       fontWeight: 700, border: "none",
                       cursor: generating ? "wait" : "pointer",
                       opacity: generating ? 0.5 : 1, borderRadius: 999 }}
            >
              <Sparkles size={11} />
              {generating ? "Generating…" : `Generate ${activeMeta.short} draft`}
            </button>
          )}
        </div>

        {loading ? (
          <p className="font-mono uppercase"
             style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                      color: "rgba(12,42,64,0.55)" }}>
            Loading…
          </p>
        ) : !activeMeta.enabled ? (
          <ComingSoonPanel channel={activeMeta} />
        ) : activeDrafts.length === 0 ? (
          <EmptyChannelPanel
            channel={activeMeta}
            onGenerate={() => generate({ angles: [activeMeta.angle] })}
            generating={generating}
          />
        ) : (
          <div className="space-y-6">
            {activeDrafts.map((d) => (
              <DraftCard
                key={d.id}
                draft={d}
                onUpdate={updateDraft}
                onAutosave={updateDraftSilent}
                onDelete={deleteDraft}
                onReload={load}
                apiToken={token}
                extraAssets={assetsForChannel(activeChannel, source.slug)}
                channelKey={activeChannel}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

/* ─── small subcomponents ─────────────────────────────────────────── */
function EmptyChannelPanel({ channel, onGenerate, generating }) {
  return (
    <div className="p-8 text-center"
         style={{ background: "#FFFFFF", border: "1px dashed rgba(12,42,64,0.25)", borderRadius: 12 }}
         data-testid={`planner-empty-${channel.key}`}>
      <Sparkles size={20} style={{ color: "#1FB6C1", margin: "0 auto" }} />
      <h3 className="mt-3" style={{ fontSize: "1rem", fontWeight: 600 }}>
        No {channel.label} draft yet
      </h3>
      <p className="mt-2 font-light max-w-md mx-auto"
         style={{ fontSize: "0.88rem", color: "rgba(12,42,64,0.65)", lineHeight: 1.55 }}>
        Generate one tailored to {channel.label}. The AI will write in the
        correct voice lane for this channel — voice rules are enforced
        server-side.
      </p>
      <button
        type="button"
        onClick={onGenerate}
        disabled={generating}
        className="mt-4 px-4 py-2 font-mono uppercase"
        style={{ background: "#1FB6C1", color: "#0C2A40",
                 fontSize: "0.65rem", letterSpacing: "0.18em",
                 fontWeight: 700, border: "none",
                 cursor: generating ? "wait" : "pointer",
                 opacity: generating ? 0.5 : 1, borderRadius: 999 }}
      >
        {generating ? "Generating…" : `Generate ${channel.label} draft`}
      </button>
    </div>
  );
}

/* ─── PushAllPanel — Buffer "send everything in one click" ─────────────
   Picks the most-recent approved/ready draft per enabled channel,
   matches it to a Buffer-connected channel (by service), and lets the
   editor schedule all of them in one POST to /api/buffer/push-all.

   Per-channel pushes are handled on each DraftCard. */
function PushAllPanel({ drafts, channels, sourceSlug, token, onPushed }) {
  const { ready, status, byService } = useBuffer();
  const [open, setOpen] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [pushing, setPushing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState("");
  const [channelChoice, setChannelChoice] = useState({});

  /* For each enabled lane, find the best candidate draft + the set of
     Buffer channels we can route it to. */
  const lanes = useMemo(() => {
    const allBufferChannels = Object.values(byService).flat();
    return channels
      .filter((c) => c.enabled)
      .map((c) => {
        const candidates = drafts
          .filter((d) => c.matches(d))
          .filter((d) => ["idea", "drafted", "approved", "scheduled", "posted"].includes(d.status || "idea"))
          .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        const draft = candidates[0] || null;
        const bufferChannels = channelsForAngle(allBufferChannels, c.angle);
        return { meta: c, draft, bufferChannels };
      });
  }, [drafts, channels, byService]);

  /* Initialise channelChoice to the first Buffer channel per lane. */
  useEffect(() => {
    setChannelChoice((prev) => {
      const next = { ...prev };
      for (const lane of lanes) {
        if (!next[lane.meta.key] && lane.bufferChannels[0]) {
          next[lane.meta.key] = lane.bufferChannels[0].id;
        }
      }
      return next;
    });
  }, [lanes]);

  const readyLanes = lanes.filter((l) => l.draft && l.bufferChannels.length > 0);

  const run = async () => {
    if (readyLanes.length === 0) {
      setError("Nothing to push — generate or approve a draft first.");
      return;
    }
    const summary = readyLanes
      .map((l) => `${l.meta.short} → ${l.bufferChannels.find((c) => c.id === channelChoice[l.meta.key])?.name || "?"}`)
      .join(", ");
    if (!window.confirm(
      `Push ${readyLanes.length} draft(s) to Buffer now?\n\n${summary}\n\n` +
      (scheduledAt
        ? `Scheduled for: ${scheduledAt}`
        : "Will be added to each channel's Buffer queue (next available slot)."),
    )) return;
    setPushing(true); setError(""); setLastResult(null);
    try {
      const selections = readyLanes.map((l) => ({
        draft_id: l.draft.id,
        channel_id: channelChoice[l.meta.key],
      }));
      const r = await fetch(`${API}/api/buffer/push-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          source_slug: sourceSlug,
          selections,
          scheduled_at: scheduledAt
            ? new Date(scheduledAt).toISOString()
            : null,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Push failed");
      setLastResult(j);
      if (j.ok > 0) onPushed();
    } catch (e) {
      setError(e.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="hv-card-soft mb-4"
         style={{ padding: 12 }}
         data-testid="planner-push-all-panel">
      <button type="button"
              onClick={() => setOpen((o) => !o)}
              data-testid="planner-push-all-toggle"
              className="w-full flex items-center justify-between gap-2"
              style={{ background: "transparent", border: "none",
                       cursor: "pointer", padding: 0 }}>
        <div className="flex items-center gap-2">
          <Send size={14} style={{ color: "#0E7A82" }} />
          <span className="font-mono uppercase"
                style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                         color: "#0E7A82", fontWeight: 700 }}>
            Push to Buffer — every channel in one click
          </span>
          {ready && status?.connected && (
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.55rem", letterSpacing: "0.18em",
                           color: "#0E7A82",
                           background: "rgba(31,182,193,0.15)",
                           padding: "2px 6px", borderRadius: 3 }}>
              connected · {status.account?.email}
            </span>
          )}
          {ready && !status?.connected && (
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.55rem", letterSpacing: "0.18em",
                           color: "#B85C38",
                           background: "rgba(184,92,56,0.10)",
                           padding: "2px 6px", borderRadius: 3 }}>
              disconnected
            </span>
          )}
        </div>
        <span className="font-mono uppercase"
              style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                       color: "rgba(12,42,64,0.55)" }}>
          {open ? "Hide" : "Open"}
        </span>
      </button>

      {open && (
        <div className="mt-3">
          {!ready && (
            <div className="font-mono uppercase"
                 style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                          color: "rgba(12,42,64,0.55)" }}>
              Checking Buffer connection…
            </div>
          )}
          {ready && !status?.connected && (
            <div className="px-3 py-2"
                 style={{ background: "rgba(184,92,56,0.10)",
                          border: "1px solid rgba(184,92,56,0.35)",
                          color: "#B85C38", fontSize: "0.82rem",
                          borderRadius: 8 }}>
              Buffer isn't connected. Add <code>BUFFER_API_KEY</code> to
              the backend <code>.env</code> and restart the API. (Reason: {status?.reason || "unknown"})
            </div>
          )}

          {ready && status?.connected && (
            <>
              <p className="mb-2 font-light"
                 style={{ fontSize: "0.82rem",
                          color: "rgba(12,42,64,0.70)", lineHeight: 1.5 }}>
                One draft per channel is selected automatically (latest
                approved or ready row). Choose which Buffer channel each
                one routes to, then push.
              </p>

              <div className="space-y-2 mb-3">
                {lanes.map((lane) => (
                  <div key={lane.meta.key}
                       className="flex items-center justify-between gap-3 flex-wrap"
                       style={{ padding: "8px 10px",
                                background: "#FFFFFF",
                                border: "1px solid rgba(12,42,64,0.10)",
                                borderRadius: 8 }}
                       data-testid={`planner-push-lane-${lane.meta.key}`}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="font-mono uppercase"
                           style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                                    color: "#0E7A82", fontWeight: 700 }}>
                        {lane.meta.short}
                      </div>
                      <div style={{ fontSize: "0.82rem", color: "#0C2A40",
                                    fontWeight: 500, lineHeight: 1.35,
                                    overflow: "hidden", textOverflow: "ellipsis",
                                    whiteSpace: "nowrap", maxWidth: 360 }}>
                        {lane.draft
                          ? (lane.draft.hook || "(no hook)")
                          : <em style={{ color: "rgba(12,42,64,0.45)" }}>No draft yet</em>}
                      </div>
                    </div>
                    {lane.bufferChannels.length > 0 ? (
                      <select
                        value={channelChoice[lane.meta.key] || ""}
                        onChange={(e) => setChannelChoice((p) => ({
                          ...p, [lane.meta.key]: e.target.value,
                        }))}
                        disabled={!lane.draft || pushing}
                        data-testid={`planner-push-channel-${lane.meta.key}`}
                        className="hv-input"
                        style={{ fontSize: "0.78rem", padding: "4px 8px",
                                 maxWidth: 240 }}
                      >
                        {lane.bufferChannels.map((bc) => (
                          <option key={bc.id} value={bc.id}>
                            {bc.name} ({bc.service})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="font-mono uppercase"
                            style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                                     color: "#B85C38" }}>
                        Not connected in Buffer
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <div className="font-mono uppercase mb-1"
                       style={{ fontSize: "0.58rem", letterSpacing: "0.20em",
                                color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
                    Schedule (optional)
                  </div>
                  <input type="datetime-local"
                         value={scheduledAt}
                         onChange={(e) => setScheduledAt(e.target.value)}
                         data-testid="planner-push-all-schedule"
                         className="hv-input"
                         style={{ fontSize: "0.82rem", padding: "4px 8px" }} />
                </div>
                <button type="button"
                        onClick={run}
                        disabled={pushing || readyLanes.length === 0}
                        data-testid="planner-push-all-run"
                        className="hv-pill is-active">
                  <Send size={11} />
                  {pushing ? "Pushing…" : `Push ${readyLanes.length} draft(s)`}
                </button>
                {scheduledAt && (
                  <button type="button" onClick={() => setScheduledAt("")}
                          disabled={pushing}
                          className="hv-pill">
                    Clear time → use queue
                  </button>
                )}
              </div>

              {error && (
                <div className="mt-2 font-mono"
                     style={{ fontSize: "0.75rem", color: "#B85C38" }}>
                  {error}
                </div>
              )}

              {lastResult && (
                <ul className="mt-3 space-y-1"
                    data-testid="planner-push-all-results">
                  {lastResult.results.map((r, i) => (
                    <li key={i}
                        className="px-3 py-1.5 flex items-center justify-between gap-2 flex-wrap"
                        style={{
                          background: r.ok ? "rgba(31,182,193,0.10)" : "rgba(184,92,56,0.10)",
                          border: `1px solid ${r.ok ? "rgba(31,182,193,0.30)" : "rgba(184,92,56,0.30)"}`,
                          color: r.ok ? "#0E7A82" : "#B85C38",
                          fontSize: "0.78rem", borderRadius: 6,
                        }}>
                      <span>
                        {r.ok ? "✓ Pushed" : "✗ Failed"} ·{" "}
                        <span className="font-mono">{r.channel_id?.slice(-6) || "—"}</span>
                        {r.post_id && <> · Buffer post <span className="font-mono">{r.post_id.slice(-8)}</span></>}
                      </span>
                      {!r.ok && r.error && (
                        <span className="font-mono"
                              style={{ fontSize: "0.7rem", opacity: 0.85 }}>
                          {r.error}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}



function ComingSoonPanel({ channel }) {
  return (
    <div className="p-8 text-center"
         style={{ background: "#FFFFFF", border: "1px dashed rgba(12,42,64,0.25)", borderRadius: 12 }}>
      <h3 style={{ fontSize: "1rem", fontWeight: 600 }}>
        {channel.label} — coming soon
      </h3>
      <p className="mt-2 font-light max-w-md mx-auto"
         style={{ fontSize: "0.88rem", color: "rgba(12,42,64,0.65)", lineHeight: 1.55 }}>
        Channel reserved. Once {channel.label} prompts and asset templates are
        ready, drafts for this channel will appear here without any further
        wiring.
      </p>
    </div>
  );
}
